import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Install-prompt state machine (issue #78).
 *
 * Computes one of:
 *   - `"installed"` — running standalone / already installed (or the
 *     `appinstalled` event fired): never prompt.
 *   - `"dismissed"` — the user dismissed the prompt within the last
 *     `DISMISSAL_DAYS` days (persisted in localStorage): don't nag.
 *   - `"eligible"` — Chromium captured `beforeinstallprompt`; a custom
 *     banner can call `install()` → the stashed `prompt()`.
 *   - `"ios"` — iOS Safari (no `beforeinstallprompt`); show the manual
 *     "Add to Home Screen" instructions instead.
 *   - `"unsupported"` — nothing to offer (e.g. desktop Firefox).
 *
 * `beforeinstallprompt` is not part of the TS DOM lib, so the event is
 * typed locally as `BeforeInstallPromptEvent` (Chromium's shape).
 */

/** How long a dismissed prompt stays dismissed (configurable). */
export const DISMISSAL_DAYS = 7;
const DISMISSAL_KEY = "thebeat.install.dismissedAt";

export type InstallStatus =
  | "installed"
  | "dismissed"
  | "eligible"
  | "ios"
  | "unsupported";

/** Chromium's `beforeinstallprompt` event (not in the DOM lib). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface InstallPromptState {
  status: InstallStatus;
  /** Trigger the browser install flow (Chrome/Android only). */
  install: () => void;
  /** Persist the dismissal and hide the prompt for DISMISSAL_DAYS. */
  dismiss: () => void;
}

export function useInstallPrompt(): InstallPromptState {
  const [status, setStatus] = useState<InstallStatus>("unsupported");
  const deferred = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const standalone = isStandalone();
    if (standalone) {
      setStatus("installed");
      return;
    }

    if (isDismissed()) {
      setStatus("dismissed");
      return;
    }

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferred.current = e as BeforeInstallPromptEvent;
      setStatus("eligible");
    };
    const onAppInstalled = () => setStatus("installed");

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    if (isIosSafari()) {
      // iOS Safari never fires beforeinstallprompt — nudge the manual
      // "Add to Home Screen" flow instead.
      setStatus("ios");
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const install = useCallback(() => {
    const event = deferred.current;
    if (!event) return;
    // `prompt()` shows the browser's install dialog; hide our banner
    // meanwhile — `appinstalled` confirms the outcome.
    void event.prompt();
    setStatus("installed");
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSAL_KEY, String(Date.now()));
    } catch {
      /* localStorage may be unavailable (private mode); fail soft. */
    }
    setStatus("dismissed");
  }, []);

  return { status, install, dismiss };
}

/** Running as an installed app (Chrome/Android + iOS `navigator.standalone`). */
function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as unknown as { standalone?: boolean }).standalone)
  );
}

/** iOS Safari: iPhone/iPad/iPod UA and not installed (no beforeinstallprompt). */
function isIosSafari(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isDismissed(): boolean {
  let dismissedAt: number | null = null;
  try {
    const raw = localStorage.getItem(DISMISSAL_KEY);
    if (raw) dismissedAt = Number(raw);
  } catch {
    return false;
  }
  if (dismissedAt === null || !Number.isFinite(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISSAL_DAYS * 24 * 60 * 60 * 1000;
}
